import { useDispatch, useSelector } from 'react-redux';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import { GenericObj } from '@solvhealth/types/interfaces/generics';
import { useGoForwardAndBack, useTabTitle, useTitles } from '.';
import { SectionNumberObj } from '../interfaces';
import { useIncrementProgress } from './useIncrementProgress';
import { useInitializePaperworkData } from './useInitializePaperworkData';
import { SectionNumber } from '../constants';
import { PGSectionNumber } from '../providerGroups/constants';
import { PaperworkFieldName, PaperworkStatus } from '../paperworkConstants';
import { PaperworkSagas } from '~/sagas/paperwork';
import { usePreloadedData } from './usePreloadedData';
import { isProviderGroupLocation } from '~/core/util/location';
import { usePaperworkContextBookingId } from '~/routes/paperwork/PaperworkBookingIdContext';
import { useAccountMatchesBooking } from './useAccountMatchesBooking';
import { useAutofillPaperwork } from './useAutofillPaperwork';

export const useSetupSection = <Sect extends SectionNumber | PGSectionNumber>(
  sectionValue: Sect,
  section: SectionNumberObj<Sect>,
  customPages: any = {}
) => {
  const preloadedData = usePreloadedData();
  const bookingId = usePaperworkContextBookingId();
  const paperwork = useSelector((state) => state.paperwork);
  const location = useSelector((state) => state.location as LocationInterface);
  const isProviderGroup = isProviderGroupLocation(location);
  const doesAccountMatchBooking = useAccountMatchesBooking();
  const autofillData = useAutofillPaperwork(bookingId);

  useTabTitle(sectionValue, section);
  useInitializePaperworkData(paperwork, bookingId, location);
  useIncrementProgress(sectionValue, paperwork.pageView, customPages);

  const [title, subtitle] = useTitles(sectionValue, section, {
    name:
      paperwork.formData[PaperworkFieldName.PATIENT_FIELD_FIRST_NAME] ??
      preloadedData?.userProfile?.first_name ??
      preloadedData?.booking?.firstName,
    guardian_name: paperwork.formData[PaperworkFieldName.GUARDIAN_FIRST_NAME],
  });

  const [goForwardAPage, goBackAPage] = useGoForwardAndBack(
    sectionValue,
    section,
    bookingId,
    paperwork.isInFinalizeEditMode
  );

  const dispatch = useDispatch();
  const handleClickNext = (newForm: GenericObj, extraFields?: GenericObj) => {
    dispatch({
      type: PaperworkSagas.SUBMIT_PAPERWORK_PAGE,
      newForm,
      bookingId,
      paperworkStatus: PaperworkStatus.IN_PROGRESS,
      onSuccess: goForwardAPage,
      isProviderGroup,
      autofilledFields: autofillData?.paperworkResponses ?? {},
      extraFields,
    });
  };

  return {
    onGoForward: () => goForwardAPage(),
    onGoBack: () => goBackAPage(),
    title,
    subtitle,
    handleClickNext,
    doesAccountMatchBooking,
  };
};
